import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { FORM_ERROR } from 'final-form';
import * as yup from 'yup';
import { CircularProgress } from '@material-ui/core';
import Form from 'ui/Form/Form';
import Button from 'ui/Button/Button';
import Link from 'ui/Link/Link';
import Input from 'ui/Input';
import NavigationTitle from 'ui/NavigationTitle/NavigationTitle';
import { DEVICE_ID, NETWORK_REQUIRED } from 'utils/constants';
import routePaths from 'routes/routePaths';
import api from 'api/api';
import { requiredOnly } from 'utils/validation';
import { OTPVerificationModal } from 'components/modals';
import { FormType } from 'components/modals/OTPVerificationModal/OTPVerificationModal';
import { mapYupObjectError, objectMap } from 'utils/helpers';
import { onlyAlphaNumeric } from 'utils/formatters';
import { IOTPError } from '../LogIn/login.model';
import { IForgotPasswordError, IForgotPasswordFormData, IForgotPasswordResponse } from './forgot-password.model';

import styles from './ForgotPassword.module.css';

const schema = yup.object().shape({
  username: requiredOnly('Username'),
});

const ForgotPassword = () => {
  const [success, setSuccess] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [isOTPModalOpen, setOTPModalOpen] = useState(false);
  const [OTPModalError, setOTPModalError] = useState<IOTPError | null>(null);

  const closeOTPModal = useCallback(() => {
    setOTPModalOpen(false);
  }, []);

  const history = useHistory();

  const validate = useCallback((values: IForgotPasswordFormData) => {
    try {
      schema.validateSync(values, { abortEarly: false });
    } catch (e) {
      const valErrs = mapYupObjectError<IForgotPasswordFormData>(e);
      return objectMap(valErrs, (v) => v[0]);
    }
    return undefined;
  }, []);

  const onOtpSubmit = useCallback(
    (otp: string | undefined, form: FormType | undefined) =>
      api
        .post(
          `customer/otp/verifyUser/${otp || ''}/false/${String(sessionStorage.getItem('userName')) || ''}`,
          {},
          true,
          false,
        )
        .then(() => {
          closeOTPModal();
          history.push(routePaths.resetPassword);
        })
        .catch((error: IOTPError) => {
          console.error(error);
          setOTPModalError(error);
          if (error.failureCount === 0 && form) {
            form.initialize({ digit1: '', digit2: '', digit3: '', digit4: '', digit5: '' });
          }
          return { [FORM_ERROR]: error.description || NETWORK_REQUIRED };
        }),
    [closeOTPModal, history],
  );

  const onSubmit = useCallback(
    (values: IForgotPasswordFormData) =>
      api
        .post('customer/validateUsername', {
          ...values,
        } as IForgotPasswordFormData)
        .then(async (response: IForgotPasswordResponse) => {
          sessionStorage.setItem('userName', values.username);
          setSuccess(response.description);
          setOTPModalOpen(true);
          const requestBody = {
            deviceId: DEVICE_ID,
            username: values.username,
          };
          try {
            await api.post('/customer/otp/generateOtp', requestBody, true, false);
          } catch (error) {
            console.error(error);
          }
        })
        .catch((error: IForgotPasswordError) => {
          console.error(error);
          setErrorMsg(error.description);
        }),
    [],
  );
  const onResend = () =>
    api.post('/customer/otp/generateOtp', {
      deviceId: DEVICE_ID,
      username: sessionStorage.getItem('userName'),
    });
  return (
    <div className={styles.content}>
      <NavigationTitle to={routePaths.login}>Forgot Password</NavigationTitle>
      <Form
        onSubmit={onSubmit}
        validate={validate}
        render={({ handleSubmit, submitting, hasValidationErrors }) => (
          <form onSubmit={handleSubmit}>
            {errorMsg ? <div className={styles.errorMsg}>{errorMsg}</div> : ''}
            <Input
              name="username"
              label="Username"
              fieldProps={{
                parse: onlyAlphaNumeric,
              }}
              disabled={submitting}
            />
            <Button type="submit" className={styles.submit} fullWidth disabled={submitting || hasValidationErrors}>
              {submitting && success ? <CircularProgress size={20} color="inherit" /> : 'Continue'}
            </Button>
            <div className={styles.policy}>
              <Link to={routePaths.privacyPolicy}>Privacy Policy</Link>
            </div>
            <OTPVerificationModal
              open={isOTPModalOpen}
              onClose={closeOTPModal}
              onSubmit={onOtpSubmit}
              onResend={onResend}
              OTPModalError={OTPModalError}
            />
          </form>
        )}
      />
    </div>
  );
};

export default ForgotPassword;
